import { Box } from '@interstate/components/Box';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';

import type { ProgramQuote } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { FC, MouseEventHandler } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectActionChip } from './styles';
import { useProgramsAndIncentivesContext } from '../ProgramsAndIncentivesContext';

export type SelectedLabelColumnBodyProps = {
    value: ProgramQuote & { key?: string };
    index: number;
    appliedProgramKey: string;
    pendingApplyKey?: string;
};

const resolveLabel = (isLenderBacked: boolean, isEstimated: boolean, isApplyingThisProgram: boolean): string => {
    if (isLenderBacked) return 'Selected';
    if (isEstimated) return 'Estimated';
    return isApplyingThisProgram ? 'Applying' : 'Select';
};

export const SelectActionColumnBody: FC<SelectedLabelColumnBodyProps> = ({ value, index, appliedProgramKey, pendingApplyKey }) => {
    const dispatch = useDispatch();
    const offerType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const pendingApplyProgram = useSelector(programsSelectors.getPendingApplyProgram);
    const isTermEstimated = useSelector(programsSelectors.getIsTermEstimatedNotLenderBacked);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isApplied = value.key !== undefined && value.key === appliedProgramKey;
    const isEstimated = isApplied && isTermEstimated;
    const isLenderBacked = isApplied && !isTermEstimated;
    const { onProgramManualSelect } = useProgramsAndIncentivesContext();
    const pendingApplyOfferType = pendingApplyProgram?.offerType;
    const isApplyingProgram = Boolean(pendingApplyProgram) && pendingApplyOfferType === offerType;
    const isApplyingThisProgram = isApplyingProgram && !isApplied && value.key !== undefined && value.key === pendingApplyKey;

    const handleColumnApplyClicked: MouseEventHandler<HTMLButtonElement> = () => {
        if (!isLenderBacked && !isApplyingProgram) {
            onProgramManualSelect();
            dispatch(
                programActionCreators.applySelectedProgram(value, {
                    forceReapply: isEstimated,
                    manualProgramSelect: true,
                    programsTableSelect: true
                })
            );
        }
    };

    return (
        <Box display={'flex'} justifyContent={'center'}>
            <SelectActionChip
                label={resolveLabel(isLenderBacked, isEstimated, isApplyingThisProgram)}
                data-testid={`select-button-${index}`}
                onClick={handleColumnApplyClicked}
                isApplied={isApplied}
                isApplying={isApplyingThisProgram}
                disabled={isLocked || isApplyingProgram}
            />
        </Box>
    );
};
